// Auto-identificação de colunas nos modais de upload (people, groups e
// occupations).
//
// Ao subir a planilha, os selects de bind coluna→atributo são pré-preenchidos
// quando o header casa com um nome conhecido — o usuário só ajusta exceções.
//
// Regras:
// - match EXATO pós-normalização (trim, lowercase, sem acentos, espaços
//   colapsados) — SEM fuzzy: falso positivo é pior que select vazio;
// - cada coluna da planilha é atribuída a UM único attribute (first-wins na
//   ordem do catálogo), espelhando o dedupe por attribute do
//   handleSelectOption;
// - catálogos derivados dos templates novos (src/assets/modelo*.xlsx, 3
//   idiomas — ver scripts/regenerate-group-templates.js), dos headers técnicos
//   idênticos nos 3 idiomas (personID, groupID, 9 colunas módulo+papel) e dos
//   headers LEGADOS (develop:src/assets/modelo.xlsx) — planilha antiga também
//   é reconhecida.
import { GROUP_MODULE_ROLE_COLUMNS } from '@/helpers/group-module-roles'
import {
  PEOPLE_ACTIONS,
  GROUPS_ACTIONS,
  OCCUPATIONS_ACTIONS,
} from '@/helpers/upload-actions'

const normalizeColumnName = value => {
  if (typeof value !== 'string') return ''

  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

// PEOPLE — headers aceitos por attribute (pt / en / es / legado develop /
// EXPORT). Além dos templates, o usuário reimporta o arquivo de EXPORT
// (SyncData-*.xlsx), cujos headers vêm do i18n do sync-backend
// (exports.current.*) e divergem dos templates em alguns nomes — ex.:
// 'Liderança Direta'/'Liderazgo Directo' (export) vs 'Líder Direto'/'Líder
// Directo' (template).
// 'Nome Completo'/'Full Name'/'Nombre Completo' é informacional (não bindável).
const PEOPLE_COLUMN_CATALOG = [
  { attribute: 'personID', headers: ['personID', 'ID'] },
  { attribute: 'code', headers: ['Código', 'Code', 'Código (CPF)'] },
  { attribute: 'firstname', headers: ['Nome', 'First Name', 'Nombre'] },
  { attribute: 'lastname', headers: ['Sobrenome', 'Last Name', 'Apellido'] },
  { attribute: 'email', headers: ['E-Mail', 'Email'] },
  {
    attribute: 'phoneNumber',
    headers: ['Telefone', 'Phone number', 'Teléfono'],
  },
  {
    attribute: 'attributes',
    headers: [
      'Atributos Demográficos',
      'Demographic Attributes',
      'Atributos Demográficos (opcional)',
    ],
  },
  {
    attribute: 'permissionGroups',
    headers: [
      // templates novos E coluna PLURAL do export
      // (exports.current.people.permissionGroups) — mesmos nomes nos 2 casos
      'Grupos de Permissionamento',
      'Permission Groups',
      'Grupos de Permisos',
      // legado (singular, pt-only: develop:modelo.xlsx). A coluna SINGULAR do
      // export (exports.current.people.permissionGroup) carrega o role
      // sistêmico e pertence ao catálogo LEGADO — não é mapeada aqui
      'Grupo de Permissionamento',
    ],
  },
  { attribute: 'language', headers: ['Idioma', 'Language'] },
  { attribute: 'status', headers: ['Status', 'Estado'] },
  {
    attribute: 'sendInvite',
    headers: ['Enviar Convite', 'Send Invite', 'Enviar Invitación'],
  },
  {
    attribute: 'birthDate',
    headers: ['Data de Nascimento', 'Birth Date', 'Fecha de Nacimiento'],
  },
  {
    attribute: 'admissionDate',
    headers: [
      'Data de Admissão',
      'Admission Date',
      'Fecha de Ingreso',
      // export es-AR emite 'Fecha de Admisión' (template usa 'Fecha de Ingreso')
      'Fecha de Admisión',
    ],
  },
  {
    attribute: 'directManager',
    headers: [
      'Líder Direto',
      'Direct Manager',
      'Líder Directo',
      // export do sync-backend (exports.current.directManager)
      'Liderança Direta',
      'Liderazgo Directo',
    ],
  },
  {
    attribute: 'occupation',
    headers: ['Profissão', 'Occupation', 'Profesión'],
  },
]

// GROUPS — headers aceitos por attribute (pt / en / es / legado develop).
//
// Hierarquia do pai no fluxo NOVO tem DUAS fontes bindáveis:
// - parentGroupID carrega o _id do grupo pai — 'Hierarquia ID' / 'Parent ID' /
//   'Jerarquía ID' (exports.current.groups.parentID no i18n do sync-backend,
//   mesmos nomes na linha 1 dos templates novos);
// - parentGroup carrega o CENTRO DE CUSTO do pai — 'Hierarquia Centro de
//   Custo' / 'Parent Cost Center' / 'Jerarquía Centro de Costos'.
// Vindo as duas preenchidas, parentGroupID VENCE — a prioridade é resolvida no
// sync-workers; aqui só garantimos que as duas podem ser ligadas. Sem o bind do
// ID a hierarquia simplesmente não voltava no reimport do próprio export: 185
// das 282 linhas do arquivo real trazem 'Hierarquia ID' SEM 'Hierarquia Centro
// de Custo', porque o pai não tem centro de custo cadastrado.
//
// 'Hierarquia Nome' / 'Parent Name' / 'Jerarquía Nombre' continua SEM
// attribute: nome não é identificador. É duplicável entre irmãos e o backend
// não tem resolução de pai por nome — auto-mapear ali ligaria a hierarquia a um
// ponteiro ambíguo, exatamente o falso positivo que o match exato evita.
//
// groupStatus liga a coluna de status do grupo — 'Status' (pt/en) / 'Estado'
// (es), conferidos em exports.current.groups.status.label nos 3 locales do
// sync-backend. A coluna era EXPORTADA e nunca consumida: não havia como
// ativar/inativar grupo por planilha, e reimportar o próprio export perdia o
// status silenciosamente. O nome do attribute é 'groupStatus', não 'status',
// para não colidir com o `status` de VERIFICAÇÃO da linha (o campo que a
// listagem de registros importados exibe) — contrato fechado com o worker.
const GROUPS_COLUMN_CATALOG = [
  { attribute: 'groupID', headers: ['groupID', 'ID'] },
  {
    attribute: 'costCenter',
    headers: ['Centro de Custo', 'Cost Center', 'Centro de Costos'],
  },
  // ordem espelha a do arquivo de EXPORT, única fonte onde a coluna existe
  // (entre o centro de custo e o nome) — os templates não têm coluna de status
  // na aba de Grupos
  { attribute: 'groupStatus', headers: ['Status', 'Estado'] },
  { attribute: 'name', headers: ['Nome', 'Name', 'Nombre'] },
  // ordem espelha a linha 1 dos templates (ID do pai antes do centro de custo
  // do pai) e a prioridade do worker — as colunas são disjuntas, sem disputa
  {
    attribute: 'parentGroupID',
    headers: ['Hierarquia ID', 'Parent ID', 'Jerarquía ID'],
  },
  {
    attribute: 'parentGroup',
    headers: [
      'Hierarquia Centro de Custo',
      'Parent Cost Center',
      'Jerarquía Centro de Costos',
    ],
  },
  // aliases globais legados (G3) + headers do EXPORT (exports.current.*):
  // 'Liderança'/'Managers'/'Liderazgo' e 'Pessoas'/'Members'/'Personas' —
  // aplicam o papel nos 3 módulos
  {
    attribute: 'managers',
    headers: ['CPF LIDERANÇA', 'Liderança', 'Managers', 'Liderazgo'],
  },
  {
    attribute: 'members',
    headers: ['CPF MEMBROS', 'Pessoas', 'Members', 'Personas'],
  },
  // colunas técnicas módulo+papel — header === attribute, nos 3 idiomas
  ...GROUP_MODULE_ROLE_COLUMNS.map(column => ({
    attribute: column,
    headers: [column],
  })),
]

// Attributes bindáveis por ação — o auto-match NUNCA pode gravar um attribute
// sem select visível na ação corrente (iria parar no payload sem o usuário ver)
const PEOPLE_ADD_UPDATE_ATTRIBUTES = [
  'personID',
  'code',
  'firstname',
  'lastname',
  'email',
  'phoneNumber',
  'attributes',
  'permissionGroups',
  'language',
  'status',
  'sendInvite',
  'birthDate',
  'admissionDate',
  'directManager',
  'occupation',
]

const PEOPLE_AUTO_MATCH_ATTRIBUTES = {
  [PEOPLE_ACTIONS.AddUpdate]: PEOPLE_ADD_UPDATE_ATTRIBUTES,
  [PEOPLE_ACTIONS.Delete]: ['email', 'personID'],
}

// As duas fontes de hierarquia só existem nas ações que aceitam hierarquia
// (full-hierarchy e partial); information-update e delete seguem sem elas.
// groupStatus vale nas TRÊS ações que atualizam grupo (full-hierarchy, partial e
// information-update) — fica fora só do delete, onde o grupo deixa de existir e
// um status no payload não teria efeito.
const GROUPS_ADD_UPDATE_ATTRIBUTES = [
  'groupID',
  'costCenter',
  'groupStatus',
  'name',
  'parentGroupID',
  'parentGroup',
  'managers',
  'members',
  ...GROUP_MODULE_ROLE_COLUMNS,
]

const GROUPS_AUTO_MATCH_ATTRIBUTES = {
  [GROUPS_ACTIONS.FullHierarchyAddUpdate]: GROUPS_ADD_UPDATE_ATTRIBUTES,
  [GROUPS_ACTIONS.PartialAddUpdate]: GROUPS_ADD_UPDATE_ATTRIBUTES,
  [GROUPS_ACTIONS.InformationUpdate]: [
    'groupID',
    'costCenter',
    'groupStatus',
    'name',
  ],
  // delete exige name E costCenter no contrato de colunas do backend
  // (ParamsListSpreadsheedDeleteVerificationGroup) — groupID é opcional.
  // Sem groupStatus: o grupo é removido, não atualizado
  [GROUPS_ACTIONS.Delete]: ['costCenter', 'name', 'groupID'],
}

// OCCUPATIONS — o módulo de profissões não tem fluxo legado/novo: o mesmo
// catálogo serve o template (src/assets/modelo*.xlsx, aba Profissões/
// Occupations/Profesiones) e o arquivo de EXPORT (exports.current.occupations.*
// no i18n do sync-backend), que emitem os MESMOS headers nos 3 idiomas.
// Sem o bind de 'ID' toda linha do reimport vira criação e o backend recusa
// com "Já existe uma profissão com este título na base de dados".
const OCCUPATIONS_COLUMN_CATALOG = [
  // 'ID' é idêntico nos 3 idiomas (template e export)
  { attribute: 'id', headers: ['ID'] },
  { attribute: 'title', headers: ['Título', 'Title'] },
  {
    attribute: 'description',
    headers: ['Descrição', 'Description', 'Descripción'],
  },
]

const OCCUPATIONS_AUTO_MATCH_ATTRIBUTES = {
  [OCCUPATIONS_ACTIONS.AddUpdate]: ['id', 'title', 'description'],
  // delete só expõe o select de ID
  [OCCUPATIONS_ACTIONS.Delete]: ['id'],
}

// ── Catálogos do fluxo LEGADO (F4, plano híbrido §3.3.3) ────────────────────
// As opções legadas dos modais aplicam o catálogo da develop: os attributes
// que a develop enviava, casados contra os headers do modelo antigo
// (develop:src/assets/modelo.xlsx, restaurado como modelo-legado.xlsx), dos
// modelos novos (3 idiomas) e do arquivo de EXPORT (3 idiomas). NUNCA mapear
// personID/groupID/permissionGroups no add-update legado — não existem lá.

// PEOPLE legado — attributes da develop (UploadModal add-update):
// code, firstname, lastname, email, attributes, role, birthDate,
// admissionDate, directManager, occupation. 'ID' e 'Nome Completo' são
// informacionais (não bindáveis).
//
// O catálogo cobre o template legado (pt), os templates novos (3 idiomas) e o
// arquivo de EXPORT (SyncData-*.xlsx, headers de exports.current.people.* no
// i18n do sync-backend) nos 3 idiomas: o usuário reimporta o export pela
// opção LEGADA ("Criar e Atualizar", sem "(Novo)") e o bind precisa casar lá
// também — era a lacuna que deixava 'Liderança Direta' (export) sem ligar em
// directManager.
const PEOPLE_LEGACY_COLUMN_CATALOG = [
  { attribute: 'code', headers: ['Código (CPF)', 'Código', 'Code'] },
  { attribute: 'firstname', headers: ['Nome', 'First Name', 'Nombre'] },
  { attribute: 'lastname', headers: ['Sobrenome', 'Last Name', 'Apellido'] },
  { attribute: 'email', headers: ['E-Mail', 'Email'] },
  // 'Grupo de Permissionamento' (SINGULAR — 1 caractere de diferença do
  // header novo 'Grupos de Permissionamento') carrega o role sistêmico
  // admin|manager|user do data-import. O export emite as DUAS colunas; o
  // match é por igualdade normalizada, então o singular daqui NUNCA casa o
  // plural do fluxo novo — vale para os 3 idiomas ('Permission Group' ×
  // 'Permission Groups', 'Grupo de Permisos' × 'Grupos de Permisos').
  {
    attribute: 'role',
    headers: [
      'Grupo de Permissionamento',
      'Permission Group',
      'Grupo de Permisos',
    ],
  },
  {
    attribute: 'birthDate',
    headers: ['Data de Nascimento', 'Birth Date', 'Fecha de Nacimiento'],
  },
  {
    attribute: 'admissionDate',
    headers: [
      'Data de Admissão',
      'Admission Date',
      // template es usa 'Fecha de Ingreso'; o export es emite 'Fecha de
      // Admisión'
      'Fecha de Ingreso',
      'Fecha de Admisión',
    ],
  },
  {
    attribute: 'directManager',
    headers: [
      'Líder Direto',
      'Direct Manager',
      'Líder Directo',
      // export do sync-backend (exports.current.people.directManager)
      'Liderança Direta',
      'Liderazgo Directo',
    ],
  },
  {
    attribute: 'occupation',
    headers: ['Profissão', 'Occupation', 'Profesión'],
  },
  {
    attribute: 'attributes',
    headers: [
      'Atributos Demográficos (opcional)',
      'Atributos Demográficos',
      'Demographic Attributes',
    ],
  },
]

const PEOPLE_LEGACY_ADD_UPDATE_ATTRIBUTES = [
  'code',
  'firstname',
  'lastname',
  'email',
  'attributes',
  'role',
  'birthDate',
  'admissionDate',
  'directManager',
  'occupation',
]

const PEOPLE_LEGACY_AUTO_MATCH_ATTRIBUTES = {
  [PEOPLE_ACTIONS.AddUpdate]: PEOPLE_LEGACY_ADD_UPDATE_ATTRIBUTES,
  // develop: delete legado só mapeia o e-mail
  [PEOPLE_ACTIONS.Delete]: ['email'],
}

// GROUPS legado — attributes da develop (UploadGroupModal):
// costCenter, name, parentGroup, managers, members (+ groupID no
// information-update via coluna 'ID' do modelo antigo).
// Mesma cobertura do catálogo de people: template legado (pt), templates
// novos (3 idiomas) e EXPORT (exports.current.groups.*, 3 idiomas).
// No legado a hierarquia segue SÓ por centro de custo: parentGroupID é
// atributo do contrato v2 (o worker só resolve o ID do pai no fluxo novo), e o
// legado permanece byte-idêntico à develop (R1) — mapear 'Hierarquia ID' aqui
// gravaria no payload legado um attribute que a develop nunca enviou.
// Vale igual para groupStatus ('Status'/'Estado'): é attribute do contrato v2 e
// a develop nunca enviou status de grupo no payload legado.
// 'Hierarquia Nome' continua sem attribute nos dois fluxos (nome não é
// identificador — ver o catálogo v2 acima).
const GROUPS_LEGACY_COLUMN_CATALOG = [
  { attribute: 'groupID', headers: ['ID'] },
  {
    attribute: 'costCenter',
    headers: ['Centro de Custo', 'Cost Center', 'Centro de Costos'],
  },
  { attribute: 'name', headers: ['Nome', 'Name', 'Nombre'] },
  {
    attribute: 'parentGroup',
    headers: [
      'Hierarquia Centro de Custo',
      'Parent Cost Center',
      'Jerarquía Centro de Costos',
    ],
  },
  // aliases globais legados (CPF *) + headers do EXPORT — aplicam o papel
  // nos 3 módulos, como no catálogo do fluxo novo
  {
    attribute: 'managers',
    headers: ['CPF LIDERANÇA', 'Liderança', 'Managers', 'Liderazgo'],
  },
  {
    attribute: 'members',
    headers: ['CPF MEMBROS', 'Pessoas', 'Members', 'Personas'],
  },
]

const GROUPS_LEGACY_ADD_UPDATE_ATTRIBUTES = [
  'costCenter',
  'name',
  'parentGroup',
  'managers',
  'members',
]

// Sem entrada para Delete: a matriz action×flow (§2.1) não tem delete legado
const GROUPS_LEGACY_AUTO_MATCH_ATTRIBUTES = {
  [GROUPS_ACTIONS.FullHierarchyAddUpdate]: GROUPS_LEGACY_ADD_UPDATE_ATTRIBUTES,
  [GROUPS_ACTIONS.PartialAddUpdate]: GROUPS_LEGACY_ADD_UPDATE_ATTRIBUTES,
  [GROUPS_ACTIONS.InformationUpdate]: ['groupID', 'costCenter', 'name'],
}

// ── Auto-seleção da aba da planilha ─────────────────────────────────────────
// Nomes esperados das abas por módulo (templates + exports, 3 idiomas).
// Mesmas regras do auto-match de colunas: match EXATO pós-normalização,
// sem fuzzy — se nenhuma aba casar, o usuário escolhe manualmente.
const PEOPLE_SHEET_TAB_NAMES = ['Pessoas', 'People', 'Personas']

const GROUPS_SHEET_TAB_NAMES = ['Grupos', 'Groups']

// Inclui as variantes pós-rename professions→occupations (F5)
const OCCUPATIONS_SHEET_TAB_NAMES = [
  'Profissões',
  'Professions',
  'Profesiones',
  'Ocupações',
  'Occupations',
  'Ocupaciones',
]

/**
 * Encontra a primeira aba da planilha cujo nome casa com um dos candidatos.
 *
 * @param {string[]} tabs nomes crus das abas do arquivo verificado
 * @param {string[]} candidates nomes esperados para o módulo
 * @returns {string|null} o nome ORIGINAL da aba casada, ou null
 */
const matchSheetTab = (tabs, candidates) => {
  const candidateKeys = new Set(
    (candidates || []).map(normalizeColumnName).filter(Boolean)
  )

  const matched = (tabs || []).find(tab =>
    candidateKeys.has(normalizeColumnName(tab))
  )

  return matched === undefined ? null : matched
}

/**
 * Casa os headers da planilha com o catálogo de headers conhecidos.
 *
 * @param {string[]} spreadsheetColumns headers crus da aba selecionada
 * @param {{attribute: string, headers: string[]}[]} catalog catálogo ordenado
 * @param {string[]} [allowedAttributes] restringe aos attributes da ação
 *   corrente (undefined/null = sem restrição)
 * @returns {{column: string, attribute: string}[]} mapeamentos no shape de
 *   tabsInformations.tab.columns
 */
const autoMatchColumns = (spreadsheetColumns, catalog, allowedAttributes) => {
  const availableColumns = new Map()

  ;(spreadsheetColumns || []).forEach(column => {
    const key = normalizeColumnName(column)
    if (key && !availableColumns.has(key)) availableColumns.set(key, column)
  })

  const matches = []
  const usedColumns = new Set()

  catalog.forEach(({ attribute, headers }) => {
    if (allowedAttributes && !allowedAttributes.includes(attribute)) return

    const matchedKey = headers
      .map(normalizeColumnName)
      .find(
        key =>
          availableColumns.has(key) &&
          !usedColumns.has(availableColumns.get(key))
      )

    if (matchedKey === undefined) return

    const column = availableColumns.get(matchedKey)
    matches.push({ column, attribute })
    usedColumns.add(column)
  })

  return matches
}

export {
  normalizeColumnName,
  autoMatchColumns,
  matchSheetTab,
  PEOPLE_SHEET_TAB_NAMES,
  GROUPS_SHEET_TAB_NAMES,
  OCCUPATIONS_SHEET_TAB_NAMES,
  PEOPLE_COLUMN_CATALOG,
  GROUPS_COLUMN_CATALOG,
  OCCUPATIONS_COLUMN_CATALOG,
  PEOPLE_AUTO_MATCH_ATTRIBUTES,
  GROUPS_AUTO_MATCH_ATTRIBUTES,
  OCCUPATIONS_AUTO_MATCH_ATTRIBUTES,
  PEOPLE_LEGACY_COLUMN_CATALOG,
  GROUPS_LEGACY_COLUMN_CATALOG,
  PEOPLE_LEGACY_AUTO_MATCH_ATTRIBUTES,
  GROUPS_LEGACY_AUTO_MATCH_ATTRIBUTES,
}
